// Angular
import { Injectable } from '@angular/core';

// Rxjs
import { Observable, throwError } from 'rxjs';

// Services
import { ApiService } from '../api/api.service';

// Models
import { User, UserHelper } from './../../models/b2b/user';
import { Product, ProductHelper } from '../../models/b2b/product';
import { ProductAlternativeHelper } from '../../models/b2b/productAlternative';
import { ProductInfo, ProductInfoHelper } from '../../models/b2b/productInfo';
import { ProductPrice } from '../../models/b2b/productPrice';
import { SearchIncrementType } from '../../models/b2b/searchIncrementType';
import { Stock, StockHelper } from '../../models/b2b/stock';

// Helpers
import { isEmptyObject } from '../../lib/helpers/utils';
import { map } from 'rxjs/operators';
import { Cart } from 'app/shared/models/b2b/cart';
import { validateApiResponse } from 'app/shared/lib/custom-rxjs-operators/api-response.operator';

@Injectable()
export class SoledadProductService {
    constructor(private api: ApiService) {}

    isOpportunitieBuyable(cart: Cart, item: Product): boolean {
        let maxReached = false;
        if (cart && cart.items && cart.items && cart.items.entities && cart.items.entities[item.info.codigo]) {
            const entityItem = cart.items.entities[item.info.codigo];
            if (entityItem.quantity >= item.cantMax) {
                maxReached = true;
            }
        }
        return maxReached;
    }

    getProductPrice(user: User, item: Product): Observable<ProductPrice[]> {
        const params = [];

        if (user.clienteCodigo != null) {
            params['CLIENTE'] = user.clienteCodigo;
        }

        if (item.id != null) {
            params['ARTICULO'] = item.info.codigo;
        }

        params['PRECIO'] = item.rates.base.precioMostrarBusqueda;
        params['UNIDADES'] = 1;

        // Call
        return this.api.callSoledad('b2bNew.dbo.HTML_ArticulosPrecioTotal', params).pipe(
            map((data) => {
                // console.log('HTML_ArticulosPrecioTotal', data);
                if (isEmptyObject(data)) {
                    let error = { message: 'Error to obtain price data' };
                    console.error(error);
                    throwError(error.message);
                }

                // const productPrice: ProductPrice[] = ProductPriceHelper.transformFromApi(
                //     data.value
                // );

                return data;
            }),
        );
    }

    getProductsDetail(id: string): Observable<Product> {
        let params = [];

        if (id != null) {
            params['BUSQUEDA'] = id;
        }

        return this.api.callSoledad('b2bNew.dbo.HTML_ArticulosBusqueda', params).pipe(
            map((data) => {
                // console.log('HTML_ArticulosDetalle', data);
                try {
                    if (isEmptyObject(data)) {
                        throw new Error('Error to obtain product detail');
                    }
                    return data.value.map((row) => ProductHelper.transformFromApi(row, this.api.environment));
                } catch (error) {
                    console.error(error);
                    throwError(error.message);
                }
            }),
        );
    }

    getProductRates(item: Product): Observable<any> {
        const params = [];

        if (item.info.codigo != null) {
            params['ARTICULO'] = item.info.codigo;
        }

        return this.api.callSoledad('b2bNew.dbo.HTML_ArticulosTarifas', params).pipe(
            map((data) => {
                try {
                    if (isEmptyObject(data)) {
                        throw new Error('Error to obtain product detail');
                    }
                    // console.log("tarifas de servicio: ", data);
                    return data;
                } catch (error) {
                    console.error(error);
                    throwError(error.message);
                }
            }),
        );
    }

    getProductUPVs(user: User, item: Product): Observable<any> {
        const params = [];

        if (user.clienteCodigo != null) {
            params['CLIENTE'] = user.clienteCodigo;
        }

        if (item.id != null) {
            params['ARTICULO'] = item.info.codigo;
        }

        // Call
        return this.api.callSoledad('b2bNew.dbo.HTML_ArticulosUpvs', params).pipe(
            map((data) => {
                if (isEmptyObject(data)) {
                    throwError('Error to obtain product UPVs');
                }
                return data;
            }),
        );
    }

    getProductMoreImages(id: string, user: User): Observable<any> {
        const params = [];

        if (id != null) {
            params['ARTICULO'] = id;
        }

        if (user && user.clienteCodigo) {
            params['CLIENTE'] = user.clienteCodigo;
        }

        // Call
        return this.api.callSoledad('b2bNew.dbo.HTML_ArticuloImagenes', params).pipe(
            map((data) => {
                // console.log('HTML_ArticuloImagenes', data);
                try {
                    if (isEmptyObject(data)) {
                        throw new Error('Error to obtain rates');
                    }
                    // return data.value.map(row => row.URL_ARTICULOS1.replace('~', '').replace(/(^\s+|\s+$)/g, ''));
                    return data.value.map((row) => ProductHelper.transformMoreImagesFromApi(row, this.api.environment));
                } catch (error) {
                    console.error(error);
                    throwError(error.message);
                }
            }),
        );
    }

    getProductMoreInfo(id: string): Observable<ProductInfo> {
        const params = [];

        if (id != null) {
            params['ARTICULOS'] = id;
        }

        // Call
        return this.api.callSoledad('b2bNew.dbo.HTML_ArticulosMasInfo', params).pipe(
            map((data) => {
                // console.log('HTML_ArticulosMasInfo', data);
                try {
                    if (isEmptyObject(data)) {
                        throw new Error('Error to obtain rates');
                    }
                    return data.value && data.value.length
                        ? ProductInfoHelper.transformFromApi(data.value[0], this.api.environment)
                        : null;
                } catch (error) {
                    console.error(error);
                    throwError(error.message);
                }
            }),
        );
    }

    getProductStock(id: string, user: User): Observable<Stock[]> {
        const params = [];

        if (id != null) {
            params['ARTICULO'] = id;
        }

        // Check if user is not Comercial
        if (user) {
            const address = UserHelper.getSelectedAddress(user);

            params['ALMACENES'] = address ? address.almacenCodigo : null;
            params['CLIENTE'] = user.clienteCodigo;
            params['DIRENVIO'] = address ? address.codigo : null;
        }

        // console.log('getProductStock', {params});

        // Call
        return this.api.callSoledad('b2bNew.dbo.HTML_ArticulosStock', params).pipe(
            map((data) => {
                // console.log('HTML_ArticulosStock', data);
                try {
                    if (isEmptyObject(data)) {
                        throw new Error('Error to obtain stock');
                    }
                    return data.value.map((row) => StockHelper.transformFromApi(row));
                } catch (error) {
                    console.error(error);
                    throwError(error.message);
                }
            }),
        );
    }

    notifyDisponibility(user: User, item: Product): any {
        const params = [];

        const defaultAddress = UserHelper.getUserDefaultAddress(user);
        const almacen = defaultAddress.agencia || null;

        // params['SESION'] = item.sesionId;
        params['ALMACEN'] = almacen;
        params['ARTICULO'] = item.info.codigo;
        // params['CANTIDAD'] = item.stock;
        // params['PRECIO'] = item.rates.base.precioMostrarBusqueda;
        // params['TIPOINCREMENTO'] = item.increment.type == SearchIncrementType.Percentage ? '%' : '€';
        // params['INCREMENTO'] = item.increment.value;

        return this.api.callSoledad('b2bNew.dbo.HTML_AvisarCuandoHayaDisponibilidad', params).pipe(
            map(
                (data) => {
                    // console.log('HTML_ArticulosAlternativas', data.value);
                    try {
                        if (isEmptyObject(data)) {
                            throw new Error('Error');
                        }
                    } catch (error) {
                        console.error(error);
                        throwError(error.message);
                    }
                    return data;
                },
                (error) => console.log({ error }),
            ),
        );
    }

    getAlternativeProducts(user: User, item: Product): Observable<any> {
        const params = [];

        const defaultAddress = UserHelper.getUserDefaultAddress(user);
        const almacen = defaultAddress.almacenCodigo || null;

        params['SESION'] = item.sesionId;
        params['ALMACEN'] = almacen;
        params['ARTICULO'] = item.info.codigo;
        params['CANTIDAD'] = item.stock;
        params['PRECIO'] = item.rates.base.precioMostrarBusqueda;
        params['TIPOINCREMENTO'] = item.increment.type === SearchIncrementType.Percentage ? '%' : '€';
        params['INCREMENTO'] = item.increment.value;

        return this.api.callSoledad('b2bNew.dbo.HTML_ArticulosAlternativas', params).pipe(
            validateApiResponse('Error obteniendo alternativas' + '. Params: ' + JSON.stringify(params)),
            map((data) => {
                // console.log('HTML_ArticulosAlternativas', data.value);
                //QUITAR
                // data = {
                //     result: 0,
                //     value: [
                //         {
                //             PREIDE: 'FA3828AF-5A36-4546-8DB5-E0E2B3295673',
                //             PREART: '0107051900047',
                //             PRECIO: 73.51,
                //             PRECIOMOSTRARBUSQUEDA: 73.51,
                //             PRECIOMOSTRARPEDIDO: 73.51,
                //             PRECIOSINDESCUENTO: 73.51,
                //             PREPRM: 378948,
                //             PREDTO1: 0,
                //             PREDTO2: 0,
                //             PREDTO3: 0,
                //             PREDTO4: 0,
                //             PREDTO5: 0,
                //             HORAS: 0,
                //             IMAGEN_MARCA: 'https://lb.solucionait.es/flex/images/marcas/hankook.png',
                //             MARCA: 'HANKOOK                       ',
                //             DESCRIPCION: '205/70X15 HANKOOK K435 96T                                  ',
                //             MODELO: 'K435                          ',
                //             DIINV: ' ',
                //             TIPO: 'TURISMO             ',
                //             IMAGEN_ARTICULO: '~/flex/images/articulos/9032.jpg',
                //             TIEMPO: 'Inmediato',
                //             EXISTENCIASFALSO: '\u003e12',
                //             EXISTENCIAS: 16.0,
                //             DIFERENCIA: 65.51,
                //             ORDEN: 65.51,
                //             CANT_MIN: 1,
                //             CANT_MULT: 1,
                //         },
                //     ],
                // };
                return data.map((row: any) => ProductAlternativeHelper.transformFromApi(row, this.api.environment));
            }),
        );
    }
}
